// Fingerprints for the Audiotool push reconciler.
//
// The push plan is rebuilt from scratch on every pass, so "did this region
// change since we last wrote it?" is answered by comparing fingerprints of
// plain data rather than by diffing objects. They must be:
//
//   • independent of object key order, at every depth — two plans built by
//     different code paths must agree;
//   • order-SENSITIVE for arrays — note order is content;
//   • identical in every runtime (extension, workers tests), so no crypto
//     APIs: two independent 32-bit FNV-1a passes, the scheme
//     shared/midi/notes.ts already uses for notesDigest. Duplicated rather
//     than imported so this module stays a leaf, the same trade notes.ts makes
//     for r4.
//
// Not cryptographic and not meant to be; it only has to be stable and
// unlikely to collide within one project's history.

/** 16 hex chars identifying `value`'s content. */
export function fingerprint(value: unknown): string {
  const s = canonicalJson(value);
  return fnv1a32(s, 0x811c9dc5).toString(16).padStart(8, "0") +
    fnv1a32(s, 0x01000193 ^ 0x811c9dc5).toString(16).padStart(8, "0");
}

/**
 * JSON with sorted object keys. `undefined` members are dropped (an absent key
 * and an undefined one mean the same under exactOptionalPropertyTypes, but a
 * spread can still produce the latter); `null` is kept. Strings stay quoted,
 * so 1 and "1" never meet.
 */
function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    // JSON.stringify(-0) is already "0"; non-finite numbers become "null",
    // which is fine — they never reach a plan (clampTempo et al. guard them).
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return `[${value.map((v: unknown) => (v === undefined ? "null" : canonicalJson(v))).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
}

function fnv1a32(s: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
